import { zodResolver } from '@hookform/resolvers/zod';

import { useForm } from 'react-hook-form';

import { DialogContentText } from '@mui/material';

import { use } from 'react';

import { Or2Dialog } from '@/components/feedback/Or2Dialog';
import { graphql, type DocumentType } from '@/gql';

import { Or2Button } from '@/components/button/Or2Button';

import { useMutation } from '@/hooks/mutationWithSnackbar';

import { PINNED_POSTS_COLLECTION_QUERY } from '@/app/(main)/g/[machineName]/noticeboard/_components/noticeboard/PinnedPostsCollection';

import { ServiceAdminMultiGroupContext } from '@/app/(main)/s/[machineName]/admin/contexts/ServiceAdminMultiGroupContext';
import { SERVICE_GROUPS_PINNED_POSTS_QUERY } from '@/app/(main)/s/[machineName]/admin/noticeboards/posts/ServiceMultiGroupPinnedPostsAccordion';

import { CurrentUserContext } from '@/context/CurrentUserContext';
import { MultiGroupContext } from '@/app/(main)/myHub/contexts/MultiGroupContext';
import { USERS_GROUPS_POSTS_QUERY } from '@/app/(main)/myHub/home/_components/MultiGroupPostsEntry';

import { POST_LIMIT } from '../../../PostCollection';
import { PostTitleForm, PostTitlePinnedSchema } from '../forms/PostTitleForm';
import type { PostTitleFormData } from '../forms/PostTitleForm';

const PIN_POST_AND_ADD_TITLE_MUTATION = graphql(`
  mutation PinPostAndAddTitle(
    $data: PinPostInput
    $titleData: AddTitleToPostInput
  ) {
    pinPost(params: $data) {
      post {
        _id
      }
    }
    addTitleToPost(params: $titleData) {
      post {
        ...postFields
      }
    }
  }
`);

const POST_ADD_TITLE_AND_PIN_DIALOG_POST_FRAGMENT = graphql(`
  fragment postAddTitleAndPinDialogPostFields on Post {
    ...postFields
  }
`);

const POST_ADD_TITLE_AND_PIN_DIALOG_GROUP_FRAGMENT = graphql(`
  fragment postAddTitleAndPinDialogGroupFields on Group {
    _id
  }
`);

interface Props {
  post: DocumentType<typeof POST_ADD_TITLE_AND_PIN_DIALOG_POST_FRAGMENT>;
  group: DocumentType<typeof POST_ADD_TITLE_AND_PIN_DIALOG_GROUP_FRAGMENT>;
  open: boolean;
  onClose: () => void;
  serviceMachineName?: string | null;
  location?: 'myHub' | 'adminHub' | null;
}

export const PostAddTitleAndPinDialog = ({
  post,
  group,
  open,
  onClose,
  serviceMachineName,
  location = null,
}: Props) => {
  const { _id: postId } = post;
  const { _id: groupId } = group;

  const { control, handleSubmit } = useForm<PostTitleFormData>({
    values: {
      title: post.title ?? '',
    },
    resolver: zodResolver(PostTitlePinnedSchema),
  });

  const refetchQueries = [];

  if (location === 'adminHub') {
    const { groupFilterIds } = use(ServiceAdminMultiGroupContext);

    refetchQueries.push({
      query: SERVICE_GROUPS_PINNED_POSTS_QUERY,
      variables: {
        machineName: serviceMachineName || '',
        first: POST_LIMIT,
        after: '',
        groupIds: groupFilterIds,
      },
    });
  } else if (location === 'myHub') {
    const { currentUserId } = use(CurrentUserContext);
    const { groupFilterIds } = use(MultiGroupContext);

    refetchQueries.push({
      query: USERS_GROUPS_POSTS_QUERY,
      variables: {
        userId: currentUserId ?? '',
        first: POST_LIMIT,
        after: '',
        groupIds: groupFilterIds,
      },
    });
  } else {
    refetchQueries.push({
      query: PINNED_POSTS_COLLECTION_QUERY,
      variables: {
        groupId,
      },
    });
  }

  const [pinPostAndAddTitle, { loading }] = useMutation(
    PIN_POST_AND_ADD_TITLE_MUTATION,
    {
      completionMessage: `Post pinned and title added`,
      onCompleted: onClose,
      refetchQueries,
      awaitRefetchQueries: true,
    },
  );

  const onSubmit = (data: PostTitleFormData) => {
    pinPostAndAddTitle({
      variables: {
        data: {
          post: postId,
        },
        titleData: {
          post: postId,
          title: data.title,
        },
      },
    });
  };

  return (
    <Or2Dialog
      title="Add a title to the post"
      open={open}
      onClose={onClose}
      actions={
        <>
          <Or2Button color="secondary" onClick={onClose}>
            Close
          </Or2Button>
          <Or2Button disabled={loading} onClick={handleSubmit(onSubmit)}>
            Save
          </Or2Button>
        </>
      }
    >
      <DialogContentText>
        In order to pin the post to the top of the noticeboard you need to give
        it a title or subject
      </DialogContentText>
      <br />
      <PostTitleForm control={control} />
    </Or2Dialog>
  );
};
